import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Interweave from 'interweave';
import IntentText from '../component/MessageComponents/IntentText';
import { compileLiveAgentTextMessage, compileTextMessage } from '../utils/messageBuilder';
import { useDispatch, useSelector } from 'react-redux';
import { editStatus } from '../redux/state/status';
import { useTranslation } from "react-i18next";
import { useSocket } from '../hook/useSocket';
import TimestampWithTooltip from './TimestampWithTooltip';

interface TextMessageProps {
    owner: 'user' | 'bot' | 'agent' | 'system' | 'null';
    message: any;
    name: string;
    time: string;
    createdBy?: number;
    isNewOwner: boolean;
    firstMessage?: boolean;
    conversationEntryId?: string; // For agent messages to track read status
    messageId?: string; // For marking bot/agent messages as "seen"
}

const TextMessage = (props: TextMessageProps) => {
    const { t } = useTranslation();
    const socket = useSocket();
    const messageRef = useRef<HTMLDivElement>(null);
    const hasSentReadRef = useRef(false);
    const hasSentSeenRef = useRef(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const isVisibleRef = useRef(false);
    const chatId = useSelector(
        (state: { session: { chatId: string } }) => state.session.chatId
    );
    const conversationId = useSelector(
        (state: { session: { conversationId: string } }) => state.session.conversationId
    );
    const accessToken = useSelector(
        (state: { session: { accessToken: string } }) => state.session.accessToken
    );
    const sessionConversationEntryId = useSelector(
        (state: { session: { conversationEntryId: string } }) => state.session.conversationEntryId
    );
    const isAuthenticated = useSelector(
        (state: { session: { isAuthenticated: boolean } }) => state.session.isAuthenticated
    );
    const chatHistoriesListStatus = useSelector(
        (state: { session: { chatHistoriesListStatus: boolean } }) => state.session.chatHistoriesListStatus
    );

    useEffect(() => {
        if (props.owner !== 'agent' && props.owner !== 'bot') {
            return;
        }

        if (!messageRef.current) return;
        if (hasSentReadRef.current) return;

        const targetConversationEntryId = props.conversationEntryId ?? sessionConversationEntryId;
        const targetMessageId = props.messageId;

        const sendReadEvent = () => {
            if (
                hasSentReadRef.current ||
                !socket?.sendMessageReadEvent ||
                !chatId ||
                !conversationId ||
                !accessToken ||
                !targetConversationEntryId ||
                document.hidden
            ) {
                return;
            }

            try {
                socket.sendMessageReadEvent({
                    chatId,
                    conversationId,
                    accessToken,
                    conversationEntryId: targetConversationEntryId,
                });

                hasSentReadRef.current = true;

                console.log('[MessageRead] sent:', targetConversationEntryId);
            } catch (err) {
                console.error('[MessageRead] error:', err);
            }
        };

        const sendSeenEvent = () => {
            if (
                hasSentSeenRef.current ||
                !socket?.sendMessageSeenEvent ||
                !isAuthenticated ||
                !chatId ||
                !targetMessageId ||
                document.hidden
            ) {
                return;
            }

            try {
                socket.sendMessageSeenEvent({
                    chatId,
                    messageId: String(targetMessageId),
                });

                hasSentSeenRef.current = true;

                console.log('[MessageSeen] sent:', targetMessageId);
            } catch (err) {
                console.error('[MessageSeen] error:', err);
            }
        };

        observerRef.current?.disconnect();

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                isVisibleRef.current = entry.isIntersecting;
                if (entry.isIntersecting) {
                    sendReadEvent();
                    sendSeenEvent();
                }
            },
            {
                threshold: 0.1,
                root: null, 
            }
        );

        observerRef.current.observe(messageRef.current);

        const rect = messageRef.current.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;

        if (inView) {
            isVisibleRef.current = true;
            sendReadEvent();
            sendSeenEvent();
        }

        const onVisibilityChange = () => {
            if (!document.hidden && isVisibleRef.current) {
                sendReadEvent();
                sendSeenEvent();
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            observerRef.current?.disconnect();
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [
        props.owner,
        props.conversationEntryId,
        props.messageId,
        sessionConversationEntryId,
        socket,
        chatId,
        conversationId,
        accessToken,
        isAuthenticated,
    ]);

    const errorResponse = useSelector(
        (state: { systemMessages: { error_response: { message: string } } }) =>
            state.systemMessages.error_response
    );

    const systemMessages = {
        errorResponse: errorResponse && errorResponse.message ? errorResponse.message : t("error_message")
    };

    let HTMLMessage: any;
    if (
        props.owner === 'null' ||
        props.owner === 'system' ||
        props.owner === 'user' ||
        props.owner === 'bot'
    ) {
        HTMLMessage = compileTextMessage(props.message, t, systemMessages, props.messageId);
    } else if (props.owner === 'agent') {
        HTMLMessage = compileLiveAgentTextMessage(props.message);
    }

    let dispatch = useDispatch();

    const openFormModal = () => {
        dispatch(editStatus({ isForm: true }));
    };

    const botAvatar = useSelector(
        (state: { settings: { botAvatar: string } }) => state.settings.botAvatar
    );

    const agentAvatar = useSelector(
        (state: { settings: { agentAvatar: string } }) =>
            state.settings.agentAvatar
    );

    const botChatBoxBgColor = useSelector(
        (state: { settings: { botChatBoxBgColor: string } }) =>
            state.settings.botChatBoxBgColor
    );
    const botTextColor = useSelector(
        (state: { settings: { botTextColor: string } }) =>
            state.settings.botTextColor
    );

    const memberChatBoxColor = useSelector(
        (state: { settings: { memberChatBoxColor: string } }) =>
            state.settings.memberChatBoxColor
    );
    const memberTextColor = useSelector(
        (state: { settings: { memberTextColor: string } }) =>
            state.settings.memberTextColor
    );

    const botFontSize = useSelector(
        (state: { settings: { botFontSize: number } }) =>
            state.settings.botFontSize
    );

    const fontSizesRef = ['xs', 'sm', 'base', 'lg', 'xl'];
    const smallFontSizeRef = ['2xs', 'xs', 'sm', 'base', 'lg'];
    const fontSizes = [
        fontSizesRef[0 + botFontSize],
        fontSizesRef[1 + botFontSize],
        fontSizesRef[2 + botFontSize],
    ];
    const smallFontSizes = [
        smallFontSizeRef[0 + botFontSize],
        smallFontSizeRef[1 + botFontSize],
        smallFontSizeRef[2 + botFontSize],
    ];

    // console.log("HTMLMessage", HTMLMessage)

    return (
        <div
            className={classNames(
                'flex w-full items-start justify-start py-0.5 font-light z-10 px-2',
                {
                    'flex-row':
                        props.owner === 'bot' || props.owner === 'agent',
                },
                {
                    'flex-row-reverse': props.owner === 'user',
                }
            )}
        >
            {/* {props.isNewOwner && props.owner === 'user' && (
                <img
                    src=""
                    alt="My profile"
                    className={classNames(
                        'w-8 h-8 medium:w-12 medium:h-12 rounded-full mt-4'
                    )}
                    // className={classNames('rounded-full mt-4')}
                />
            )} 
             {!props.isNewOwner && props.owner === 'user' && (
                <img
                    src=""
                    alt="My profile"
                    className={classNames(
                        'w-8 h-0 medium:w-12 medium:h-0 rounded-full invisible mt-2'
                    )}
                    // className={classNames('rounded-full invisible mt-2')}
                />
            )}*/}
            {botAvatar && props.isNewOwner && props.owner === 'bot' && (
                // <div className="w-[24px] h-8 min-w-8 min-h-8 medium:w-12 medium:h-12 mt-4 rounded-full shadow-md overflow-hidden">
                //     <img
                //         src={botAvatar}
                //         alt="My profile"
                //         className={classNames(
                //             'w-[24px] h-8 medium:w-12 medium:h-12 object-fill'
                //         )}
                //     />
                // </div>
                <img
                    src={botAvatar}
                    alt="My profile"
                    className={classNames(
                        'w-8 h-8 medium:w-12 medium:h-12 object-fill mt-4 rounded-full shadow-lg'
                    )}
                    // className={classNames('rounded-full mt-4')}
                />
            )}
            {botAvatar && !props.isNewOwner && props.owner === 'bot' && (
                <img
                    src=""
                    alt="My profile"
                    className={classNames(
                        'w-8 h-0 medium:w-12 medium:h-0 rounded-full invisible mt-2'
                    )}
                    // className={classNames('rounded-full invisible mt-2')}
                />
            )}
            {agentAvatar && props.isNewOwner && props.owner === 'agent' && (
                <img
                    src={agentAvatar}
                    alt="My profile"
                    className={classNames(
                        'w-8 h-8 medium:w-12 medium:h-12 rounded-full mt-4 shadow-md'
                    )}
                    // className={classNames('rounded-full mt-4')}
                />
            )}
            {agentAvatar && !props.isNewOwner && props.owner === 'agent' && (
                <img
                    src=""
                    alt="My profile"
                    className={classNames(
                        'w-8 h-0 medium:w-12 medium:h-0 rounded-full invisible mt-2'
                    )}
                    // className={classNames('rounded-full invisible mt-2')}
                />
            )}
            <div
                className={classNames(
                    'w-full flex flex-col text-xs px-2',
                    {
                        'items-start':
                            props.owner === 'bot' || props.owner === 'agent',
                    },
                    { 'items-end': props.owner === 'user' }
                )}
            >
                <div
                    className={`text-${smallFontSizes[0]} small:text-${smallFontSizes[0]} medium:text-${smallFontSizes[1]} large:text-${smallFontSizes[2]} font-medium text-slate-500 mt-2`}
                >
                    {`${props.firstMessage ? props.name : ''} `}
                    <TimestampWithTooltip
                        time={props.time}
                        createdBy={props.createdBy}
                    />
                </div>
                {(props.owner === 'bot' || props.owner === 'agent') && (
                    <div
                        ref={messageRef}
                        style={{
                            backgroundColor: `${
                                botChatBoxBgColor
                                    ? botChatBoxBgColor.slice(0, 7)
                                    : null
                            }`,
                            color: `${
                                botTextColor ? botTextColor.slice(0, 7) : null
                            }`,
                        }}
                        className={classNames(
                            `w-fit max-w-90pc text-${fontSizes[0]} font-base small:text-${fontSizes[0]} medium:text-${fontSizes[1]} large:text-${fontSizes[2]} px-4 py-2 rounded-lg inline-block break-words shadow-md chat--disapear active flex flex-wrap rounded-tl-none whitespace-normal`,
                            {
                                'bg-slate-500': !botChatBoxBgColor,
                                'text-white': !botTextColor,
                            }
                        )}
                    >
                        {/* <Interweave content={HTMLMessage}></Interweave> */}
                        {HTMLMessage}
                    </div>
                )}
                {props.owner === 'user' && (
                    <div
                        style={{
                            backgroundColor: `${
                                memberChatBoxColor
                                    ? memberChatBoxColor.slice(0, 7)
                                    : null
                            }`,
                            color: `${
                                memberTextColor
                                    ? memberTextColor.slice(0, 7)
                                    : null
                            }`,
                        }}
                        className={classNames(
                            `w-fit max-w-90pc text-${fontSizes[0]} font-base small:text-${fontSizes[0]} medium:text-${fontSizes[1]} large:text-${fontSizes[2]} px-4 py-2 rounded-lg inline-block break-words shadow-md chat--disapear active flex flex-wrap rounded-tr-none whitespace-normal`,
                            {
                                'bg-slate-500': !memberChatBoxColor,
                                'text-white': !memberTextColor,
                            }
                        )}
                    >
                        {/* <Interweave content={HTMLMessage}></Interweave> */}
                        {HTMLMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

TextMessage.propTypes = {};

export default TextMessage;
